import React from 'react';
import Typography, { type TypographyProps } from '@mui/material/Typography';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import { useAppSelector } from '../../redux/hooks';
import { selectDirection } from '../../redux/selectors';

const defaultAllowedTags = ['br', 'img'];
const defaultAllowedAttr = ['src', 'alt'];

interface SanitizedParagraphProps {
    content: string;
    variant?: TypographyProps['variant'];
    component?: React.ElementType | '';
    fontFamily?: string;
    textAlign?: string;
    fontWeight?: number;
    fontSize?: string | number;
    lineHeight?: string | number;
    customTags?: string[];
    customAttributes?: Record<string, string[]>;
    color?: string;
    direction?: string;
}

const SanitizedParagraph: React.FC<SanitizedParagraphProps> = ({content, variant, component, fontFamily, textAlign = 'start', fontWeight = 600, fontSize, lineHeight, customTags = [], customAttributes = {}}) => {
    const direction = useAppSelector(selectDirection)
    
    let processedContent = content;
    if (typeof content === 'string') {
        processedContent = content
            .replace(/<p[^>]*>/gi, '')
            .replace(/<\/p>/gi, '<br>')
            .replace(/<div[^>]*>/gi, '')
            .replace(/<\/div>/gi, '<br>');
    }
    
    const allowedTags = [...defaultAllowedTags, ...customTags];
    const allowedAttr = [...new Set([...defaultAllowedAttr, ...Object.values(customAttributes).flat()])];
    const sanitizedContent = DOMPurify.sanitize(processedContent, { ALLOWED_TAGS: allowedTags, ALLOWED_ATTR: allowedAttr });

    const parsedContent = parse(sanitizedContent);


    return (
        <Typography 
            variant={variant}
            {...(component ? { component } : {})}
            fontWeight={fontWeight}
            fontSize={fontSize}
            lineHeight={lineHeight}
            sx={{
                textAlign: textAlign,
                fontFamily: fontFamily? fontFamily : '',
                direction,
                '& p': {
                    margin: 0
                }
            }}
        >
            {parsedContent}
        </Typography>
    );
};

export default SanitizedParagraph;
